import * as React from 'react';
import { Clip } from '@sravni/react-icons';
import { useBoolean, useStyles, useMergedRef } from '@sravni/react-utils';
import { Alert } from '../../Alert/src/Alert.js';
import { Button } from '../../Button/src/Button.js';
import { Icon } from '../../Icon/src/Icon.js';
import { Space } from '../../Space/src/Space.js';
import '../../Typography/src/Heading.js';
import '../../Typography/src/Link.js';
import '../../Typography/src/ListItem.js';
import '../../Typography/src/OrderedList.js';
import { Text } from '../../Typography/src/Text.js';
import '../../Typography/src/UnorderedList.js';
import modules_64347241 from '../styles/Uploader.module.less.js';

/**
 * @description Uploader component.
 *
 * @component
 * @example
 * ```jsx
 * <Uploader title="title">
 *   <Uploader.Item key={fileKey} file={file}/>
 * </Uploader>
 * ```
 */
const Uploader = React.forwardRef((props, ref) => {
    const { title, description, dragZoneDescription, dragZonePreview, buttonCaption, multiple, accept, disabled, className, children, onChange, onFilesUpload, onDragOver, onDragEnter, onDragLeave, onDrop, errorMessage, ...restProps } = props;
    const [isDragActive, setIsDragActive] = useBoolean(false);
    const dropZoneRef = React.useRef(null);
    const hiddenFileInput = React.useRef(null);
    const cx = useStyles(modules_64347241);
    const uploaderStyles = cx('upload-container', {
        disabled,
        error: errorMessage,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'drop-zone-active': isDragActive,
    });
    const uploadCaptionClassNames = cx('upload-caption', {
        disabled,
    });
    const uploadFiles = React.useCallback((files) => {
        if (!files.length)
            return;
        const filesToUpload = [];
        const count = multiple ? files.length : 1;
        const innerAccept = accept?.replace(/\s/g, '').split(',');
        for (let i = 0; i < count; i++) {
            const file = files.item(i);
            if (!file) {
                continue;
            }
            if (innerAccept) {
                innerAccept.some((accept) => file?.type.match(accept)) && filesToUpload.push(file);
            }
            else {
                filesToUpload.push(file);
            }
        }
        onFilesUpload?.(filesToUpload);
    }, [accept, multiple, onFilesUpload]);
    const handleDragEnter = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        onDragEnter?.(event);
        if (event.dataTransfer?.items && event.dataTransfer.items.length > 0) {
            setIsDragActive.on();
        }
    }, [onDragEnter, setIsDragActive]);
    const handleDragLeave = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        onDragLeave?.(event);
        setIsDragActive.off();
    }, [onDragLeave, setIsDragActive]);
    const handleDragOver = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        onDragOver?.(event);
        !isDragActive && setIsDragActive.on();
    }, [isDragActive, onDragOver, setIsDragActive]);
    const handleDrop = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive.off();
        onDrop?.(event);
        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            uploadFiles(event.dataTransfer.files);
            event.dataTransfer.clearData();
        }
    }, [onDrop, setIsDragActive, uploadFiles]);
    const handleClick = React.useCallback(() => !disabled && hiddenFileInput.current && hiddenFileInput.current.click(), [disabled, hiddenFileInput]);
    const handleChange = React.useCallback((event) => {
        const fileList = event.target.files;
        onChange?.(event);
        if (!fileList)
            return;
        uploadFiles(fileList);
    }, [uploadFiles, onChange]);
    const uploadedFiles = React.useMemo(() => {
        if (!React.Children.count(children))
            return;
        return (React.createElement(Space, { size: 8, direction: "vertical" }, React.Children.map(children, (child) => {
            if (child == null)
                return;
            return React.cloneElement(child, { ...child.props });
        })));
    }, [children]);
    React.useEffect(() => {
        const ref = dropZoneRef?.current;
        if (ref && !disabled) {
            ref.addEventListener('dragenter', handleDragEnter);
            ref.addEventListener('dragleave', handleDragLeave);
            ref.addEventListener('dragover', handleDragOver);
            ref.addEventListener('drop', handleDrop);
        }
        return () => {
            ref?.removeEventListener('dragenter', handleDragEnter);
            ref?.removeEventListener('dragleave', handleDragLeave);
            ref?.removeEventListener('dragover', handleDragOver);
            ref?.removeEventListener('drop', handleDrop);
        };
    }, [disabled, dropZoneRef, handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);
    return (React.createElement(Space, { "data-qa": "Uploader", className: cx(className), size: 16, direction: "vertical" },
        title && React.createElement("div", { className: cx('title') }, title),
        description && React.createElement(Alert, { color: "orange" }, description),
        React.createElement(Space, { direction: "vertical", size: 8 },
            React.createElement("div", { "data-qa": "DropZone", ref: dropZoneRef, className: uploaderStyles, onClick: handleClick },
                React.createElement("input", { ref: useMergedRef(hiddenFileInput, ref), type: "file", className: cx('upload-input'), accept: accept, multiple: multiple, disabled: disabled, onChange: handleChange, ...restProps }),
                (multiple || !React.Children.count(children)) && (React.createElement(Space, { size: 12, align: "center" },
                    dragZonePreview && React.createElement("img", { className: cx('uploader-preview'), src: dragZonePreview, alt: "preview" }),
                    React.createElement(Text, { className: uploadCaptionClassNames }, dragZoneDescription),
                    React.createElement(Button, { type: "button", disabled: disabled, className: cx('upload-button') },
                        React.createElement(Icon, { icon: React.createElement(Clip, null) })))),
                uploadedFiles),
            errorMessage && (React.createElement(Text, { size: 12, className: "h-color-R100" }, errorMessage)))));
});
Uploader.defaultProps = {
    dragZoneDescription: 'Переместите сюда файлы или загрузите с помощью кнопки',
    buttonCaption: 'Загрузить',
};
Uploader.displayName = 'Uploader';

export { Uploader };
//# sourceMappingURL=Uploader.js.map
